import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Quote
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';
import PortfolioSection from '../components/PortfolioSection';
import NewsSection from '../components/NewsSection';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// HERO RIGHT-SIDE VISUAL
// Switch between the static promo image (current) and the earlier animated
// portfolio-tile grid by changing HERO_VISUAL to 'grid'. Both are kept below
// so we can flip back at any time without re-writing the code.
//   'image' -> static promo image that links out (current)
//   'grid'  -> animated 2x2 flipping grid of portfolio company images (previous design)
// ============================================
const HERO_VISUAL = 'image';

// Static promo images (used when HERO_VISUAL === 'image'). Each has its own image, alt text,
// screen-reader label and link. Point HERO_PROMO_IMAGE at a different entry to switch.
// (The two DeepTech images are the same article: dark background is the newer one.)
const HERO_PROMOS = {
  rdifCohort: {
    src: '/images/rdif-deeptech-first-cohort.webp',
    alt: 'RDIF to disburse Rs 7,131 cr for DeepTech investments: YourNest selected in first cohort. Read more.',
    label: 'Read: RDIF to disburse Rs 7,131 cr for DeepTech investments, YourNest selected in first cohort (opens in new tab)',
    href: 'https://yournest.in/news?open=news_6fb41c68',
  },
  artOrScienceDark: {
    src: '/images/deeptech-art-or-science-dark.webp',
    alt: 'Is DeepTech Investing an Art or a Science? Read more.',
    label: 'Read: Is DeepTech Investing an Art or a Science? (opens in new tab)',
    href: 'https://yournest.in/insights/the-art-science-of-deeptech-investing',
  },
  artOrScienceRed: {
    src: '/images/deeptech-art-or-science.webp',
    alt: 'Is DeepTech Investing an Art or a Science? Read more.',
    label: 'Read: Is DeepTech Investing an Art or a Science? (opens in new tab)',
    href: 'https://yournest.in/insights/the-art-science-of-deeptech-investing',
  },
};
const HERO_PROMO_IMAGE = HERO_PROMOS.rdifCohort;

// Hero tile images (2x2 grid cycles through these 9) — only used when HERO_VISUAL === 'grid'
const HERO_TILE_IMAGES = [1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => `/images/hero-tiles/${n}.png`);
const HERO_TILE_ALTS = [1, 2, 3, 4, 5, 6, 7, 8, 9].map(
  (n) => `Decorative tiled product image from a YourNest portfolio company (image ${n} of 9)`
);
const HERO_CYCLE_INTERVAL_MS = 5000;
const HERO_FLIP_DURATION_S = 1.15;
const HERO_FLIP_EASE = [0.22, 1, 0.36, 1];

// Random flip direction per tile: +1 (flip right) or -1 (flip left)
const useFlipDirection = () => {
  const dir = useRef(null);
  if (dir.current === null) dir.current = Math.random() < 0.5 ? 1 : -1;
  return dir.current;
};

// Pick next index for tile i: must be different from current tile and not used by others
function pickNextDistinctIndex(currentIndices, tileIndex) {
  const used = new Set(currentIndices);
  const available = [0, 1, 2, 3, 4, 5, 6, 7, 8].filter((k) => !used.has(k));
  if (available.length === 0) return currentIndices[tileIndex];
  return available[Math.floor(Math.random() * available.length)];
}

// Single tile: flips on Y axis every 5s
const HeroFlipTile = ({ cellIndex, currentIndex, getNextIndex, onFlipComplete }) => {
  const [rotateY, setRotateY] = useState(0);
  const [backIndex, setBackIndex] = useState(null);
  const [displayIndex, setDisplayIndex] = useState(currentIndex);
  const flippedToBackRef = useRef(false);
  const flipDir = useFlipDirection();
  const flipTarget = flipDir * 180;

  useEffect(() => {
    if (rotateY === 0) setDisplayIndex(currentIndex);
  }, [currentIndex, rotateY]);

  useEffect(() => {
    const t = setInterval(() => {
      const next = getNextIndex(cellIndex);
      setBackIndex(next);
      flippedToBackRef.current = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setRotateY(flipTarget));
      });
    }, HERO_CYCLE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [cellIndex, getNextIndex, flipTarget]);

  const showBack = backIndex !== null;

  return (
    <div className="relative w-full h-full p-0" style={{ backgroundColor: '#FAFAFA' }}>
      <motion.div
        className="relative w-full h-full p-0"
        style={{
          transformStyle: 'preserve-3d',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          willChange: 'transform',
          backgroundColor: '#FAFAFA',
          perspective: '1200px',
        }}
        animate={{ rotateY }}
        transition={{
          duration: rotateY === 0 ? 0 : HERO_FLIP_DURATION_S,
          ease: HERO_FLIP_EASE,
        }}
        onAnimationComplete={() => {
          if (flippedToBackRef.current) {
            flippedToBackRef.current = false;
            if (backIndex !== null) setDisplayIndex(backIndex);
            setRotateY(0);
            onFlipComplete(cellIndex);
          }
        }}
      >
        <div
          className="absolute inset-0 p-0"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(0deg) translateZ(0.1px)',
            backgroundColor: '#FAFAFA',
          }}
        >
          <img
            src={HERO_TILE_IMAGES[displayIndex]}
            alt={HERO_TILE_ALTS[displayIndex]}
            aria-label={HERO_TILE_ALTS[displayIndex]}
            className="w-full h-full object-contain block"
            draggable={false}
          />
        </div>
        <div
          className="absolute inset-0 p-0"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: `rotateY(${flipTarget}deg) translateZ(0.1px)`,
            backgroundColor: '#FAFAFA',
          }}
        >
          {showBack && (
            <img
              src={HERO_TILE_IMAGES[backIndex]}
              alt={HERO_TILE_ALTS[backIndex]}
              aria-label={HERO_TILE_ALTS[backIndex]}
              className="w-full h-full object-contain block"
              draggable={false}
            />
          )}
        </div>
      </motion.div>
    </div>
  );
};

// ============================================
// HERO SECTION
// ============================================
const HeroSection = () => {
  const [tileIndices, setTileIndices] = useState([0, 1, 2, 3]);
  const tileIndicesRef = useRef([0, 1, 2, 3]);

  useEffect(() => {
    if (HERO_VISUAL !== 'grid') return;
    HERO_TILE_IMAGES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const getNextIndex = useCallback((tileIndex) => {
    const prev = tileIndicesRef.current.slice();
    const next = pickNextDistinctIndex(prev, tileIndex);
    prev[tileIndex] = next;
    tileIndicesRef.current = prev;
    setTileIndices([...prev]);
    return next;
  }, []);

  const onFlipComplete = useCallback(() => {}, []);

  return (
    <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-[#FAFAFA] via-white to-[#FDF8F7]">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating geometric shapes */}
        <motion.div
          className="absolute top-20 right-[10%] w-72 h-72 rounded-full bg-[#C8473A]/5"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute bottom-20 left-[5%] w-96 h-96 rounded-full bg-[#C8473A]/3"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [90, 0, 90],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute top-1/3 left-1/4 w-48 h-48 rounded-full bg-[#B74F40]/5"
          animate={{
            y: [0, -30, 0],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-40 left-[60%] w-32 h-32 rounded-full bg-[#C8473A]/4"
          animate={{
            x: [0, 20, 0],
            y: [0, -20, 0],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(200,71,58,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(200,71,58,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Badge className="bg-[#C8473A]/10 text-yn-brand-on-tint hover:bg-[#C8473A]/10 mb-6 px-4 py-2">
              <span className="w-2 h-2 rounded-full bg-[#C8473A] animate-pulse mr-2" />
              We Nurture Success
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#211F1F] leading-tight mb-6">
              Nurturing DeepTech Founders from{' '}
              <span className="text-[#C8473A]">Idea to Success</span>
            </h1>
            
            <p className="text-lg text-gray-600 mb-8 max-w-xl leading-relaxed">
              YourNest partners with &apos;Challengineer&apos; founders at an early stage. We invest financial capital along with nurturance, and provide a network of advisors and mentors. Together, we build and scale IP-led tech startups from India.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                className="bg-[#C8473A] hover:bg-[#B23D32] text-white px-8 rounded-full group"
                asChild
              >
                <a href="https://form.jotform.com/201753044520444" target="_blank" rel="noopener noreferrer" aria-label="Pitch to Us (opens in new tab)">
                  Pitch to Us
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </a>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-[#211F1F] text-[#211F1F] hover:bg-[#211F1F] hover:text-white px-8 rounded-full"
                asChild
              >
                <Link to="/portfolio">
                  View Portfolio
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Content - DeepTech investing article promo image / portfolio tile grid */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative w-full max-w-md mx-auto lg:max-w-none lg:mx-0 lg:w-[85%]"
          >
            {HERO_VISUAL === 'grid' ? (
              <div className="grid grid-cols-2 gap-0 aspect-square rounded-2xl overflow-hidden shadow-2xl">
                {[0, 1, 2, 3].map((cellIndex) => (
                  <div
                    key={cellIndex}
                    className="relative overflow-hidden"
                  >
                    <HeroFlipTile
                      cellIndex={cellIndex}
                      currentIndex={tileIndices[cellIndex]}
                      getNextIndex={getNextIndex}
                      onFlipComplete={onFlipComplete}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <a
                href={HERO_PROMO_IMAGE.href}
                target="_blank"
                rel="noopener noreferrer"
                className="block aspect-square rounded-2xl overflow-hidden shadow-2xl"
                aria-label={HERO_PROMO_IMAGE.label}
              >
                <img
                  src={HERO_PROMO_IMAGE.src}
                  alt={HERO_PROMO_IMAGE.alt}
                  className="w-full h-full object-cover"
                />
              </a>
            )}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        <div className="w-6 h-10 border-2 border-[#211F1F]/30 rounded-full flex justify-center pt-2">
          <motion.div
            className="w-1.5 h-1.5 bg-[#C8473A] rounded-full"
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>
      </motion.div>
    </section>
  );
};

// ============================================
// FUNDS SECTION (static content, reverse chronological)
// ============================================
const FUND_TILES = [
  {
    name: 'CF I',
    full_name: 'YourNest Continuum Fund I',
    launch_year: 2026,
    size_display: 'INR 394 cr',
    description:
      'A specialised Secondaries Vehicle to transition mature, high-potential assets nearing the end of traditional fixed-term fund lifecycles into an extended patient-capital framework.',
    highlight: 'Anchored by HDFC AMC Select FOF I',
  },
  {
    name: 'Fund III',
    full_name: 'YourNest Innovative Products VC Fund III',
    launch_year: 2021,
    size_display: 'INR 504 cr',
    description: 'Indian DeepTech: AI, SpaceTech, Quantum Computing, Energy Management, IoT, Robotics',
    highlight: 'Anchored by NIIF',
  },
  {
    name: 'Fund II',
    full_name: 'YourNest India VC Fund II',
    launch_year: 2017,
    size_display: 'INR 215 cr',
    description: 'Enterprise-oriented tech opportunities: digitising life and work, smart physical systems',
    highlight: 'Anchored by Electronics Development Fund (Govt. of India)',
  },
  {
    name: 'Fund I',
    full_name: 'YourNest Angel Fund',
    launch_year: 2012,
    size_display: 'INR 83 cr',
    description: 'Tech-enabled sectors: Conversational AI, Mobile, Internet, Cloud, Software, Analytics',
    highlight: "Preqin's Top 10 Alternative Assets in India",
  },
];

const FundsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section className="py-24 bg-[#FAFAFA]" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 items-stretch">
          {FUND_TILES.map((fund, index) => (
            <motion.div
              key={fund.name}
              initial={{ opacity: 0, y: 24 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 lg:p-7 shadow-sm border border-gray-100 flex flex-col h-full"
            >
              <p className="text-[#C8473A] font-semibold text-sm uppercase tracking-wider mb-2">
                {fund.name} • {fund.launch_year}
              </p>
              <h3 className="heading-subsection font-bold mb-3 min-h-[3.5rem] leading-snug">
                {fund.full_name}
              </h3>
              <p className="text-lg lg:text-xl font-bold text-[#211F1F] mb-3 tabular-nums">
                {fund.size_display}
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-3 flex-grow">
                {fund.description}
              </p>
              {fund.highlight && (
                <p className="text-gray-500 text-sm italic border-t border-gray-100 pt-3 mt-auto">
                  {fund.highlight}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// PARTNERSHIP SECTION
// ============================================
const PartnershipSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const benefits = [
    'Hands-on mentorship from specialist partners with decades of combined experience',
    'Strategic guidance on tech deployment, product development, brand-building, go-to-market, scaling, people development, financial best practices and governance',
    'Support in follow-on fundraising from our extensive investor network',
    'Access to operational resources and best practices',
    'Direct access to a community of industry leaders and successful entrepreneurs',
    'Assistance in recruiting top-tier talent for critical early hires',
  ];

  return (
    <section className="py-24 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src="/images/partnership-hands-plant.png"
                alt="Several hands holding a plant (credit: Shutterstock)"
                className="w-full h-[500px] object-cover"
              />
              {/* Overlay Card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl">
                <div className="text-sm text-gray-500 uppercase tracking-wider mb-1">Typical Investment</div>
                <div className="text-2xl font-bold text-[#211F1F]">$500k - $1.5 mn</div>
              </div>
            </div>
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-[#211F1F] mb-6 leading-tight">
              More than capital.
              <span className="block text-[#C8473A]">A true partnership.</span>
            </h2>
            
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              When you partner with YourNest, you gain more than an investor. You gain a dedicated team committed to nurturing your success.
            </p>
            
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#C8473A]/10 flex items-center justify-center mt-0.5">
                    <Check className="w-4 h-4 text-[#C8473A]" />
                  </div>
                  <p className="text-gray-700">{benefit}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// TESTIMONIALS SECTION
// ============================================
const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const testimonials = [
    {
      fundLine: (
        <p className="text-[#C8473A] font-semibold text-sm tracking-wider mb-4 relative z-10">
          <span className="uppercase">Fund II</span>
          <span className="uppercase"> AND </span>
          <span className="uppercase">Fund III</span>
        </p>
      ),
      quote:
        "YourNest stands out for its unique mix of entrepreneurial DNA and hands-on operational experience. They provide the nurture capital early-stage startups crave and have the nerve to back truly ambitious DeepTech moonshots. From day zero, they've been co-dreamers and empathetic partners through the highs and lows of scaling.",
      name: "Arun Vinayak",
      title: "Co-Founder",
      company: "Exponent Energy",
      portfolio_company_id: "Exponent",
    },
    {
      fund: "Fund III",
      quote:
        "YourNest partnership brings sharp focus on the near-term (AOP) and an even more powerful eye on the big dream. 'VTION (pun intended) is a quantified dream', underlines their DNA.",
      name: "Manoj Dawane",
      title: "Founder and CEO",
      company: "VTION",
      portfolio_company_id: "VTION",
    },
    {
      fund: "Fund III",
      quote:
        "With YourNest, capital came with wisdom. Each partner brought deep, multidimensional expertise, strengthening all facets of our startup and playing a pivotal role in our journey.",
      name: "Saniya Jeswani",
      title: "Founder and CEO",
      company: "Perkant Tech",
      portfolio_company_id: "Perkant",
    },
  ];

  return (
    <section className="py-24 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-[#211F1F] mb-6">
            <span className="text-[#C8473A]">Trusted</span> by Founders
          </h2>
          <p className="text-lg text-gray-600">
            Hear from the entrepreneurs we've partnered with on their journey to success.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="bg-[#FAFAFA] rounded-2xl p-8 relative flex flex-col h-full"
            >
              {/* Quote Icon */}
              <div className="absolute top-6 right-6 text-[#C8473A]/20">
                <Quote className="w-10 h-10" />
              </div>

              {/* Fund label */}
              {testimonial.fundLine ? (
                testimonial.fundLine
              ) : testimonial.fund ? (
                <p className="text-[#C8473A] font-semibold text-sm uppercase tracking-wider mb-4 relative z-10">
                  {testimonial.fund}
                </p>
              ) : null}
              
              {/* Quote - flex-grow so author block aligns at bottom */}
              <div className="flex-grow min-h-0 relative z-10">
                <p className="text-gray-700 leading-relaxed mb-8">
                  &ldquo;{testimonial.quote}&rdquo;
                </p>
              </div>
              
              {/* Author - no profile picture; company name links to portfolio */}
              <div className="mt-auto">
                <div className="font-semibold text-[#211F1F]">{testimonial.name}</div>
                <div className="text-sm text-gray-500">
                  {testimonial.title},{' '}
                  {testimonial.portfolio_company_id ? (
                    <Link
                      to={`/portfolio?open=${encodeURIComponent(testimonial.portfolio_company_id)}`}
                      className="text-[#C8473A] hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {testimonial.company}
                    </Link>
                  ) : (
                    testimonial.company
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// FOUNDER CTA SECTION (bottom of homepage)
// ============================================
const FounderCTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section className="py-24 bg-[#211F1F] text-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to build something extraordinary?
          </h2>
          <p className="text-lg text-gray-300 mb-10 leading-relaxed">
            If you&apos;re a founder with a bold vision, and the determination to make it reality,<br />
            we want to hear from you.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-[#C8473A] hover:bg-[#B74F40] text-white px-8 py-6 rounded-full text-lg font-medium transition-all hover:shadow-xl hover:shadow-[#C8473A]/25 group"
          >
            <a href="https://form.jotform.com/201753044520444" target="_blank" rel="noopener noreferrer" aria-label="Pitch to Us (opens in new tab)">
              Pitch to Us
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform inline-block" aria-hidden="true" />
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// MAIN LANDING PAGE
// ============================================
const LandingPage = () => {
  usePageTitle('');
  // Avoid useScroll() here: it subscribes to layout/scroll in ways that have surfaced
  // hook-related runtime errors in production (React 19). Plain scroll + rAF is stable.
  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    let rafId = 0;
    const update = () => {
      rafId = 0;
      const el = document.documentElement;
      const scrollable = el.scrollHeight - window.innerHeight;
      const p =
        scrollable > 0
          ? Math.min(1, Math.max(0, window.scrollY / scrollable))
          : 0;
      setScrollProgress(p);
    };
    const onScrollOrResize = () => {
      if (!rafId) rafId = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA] overflow-x-hidden">
      {/* Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-[#C8473A] origin-left z-[100]"
        style={{ scaleX: scrollProgress }}
      />
      
      <Header />
      
      <main id="main-content" tabIndex={-1}>
        <HeroSection />
        <FundsSection />
        <PartnershipSection />
        <PortfolioSection />
        <TestimonialsSection />
        <NewsSection />
        <FounderCTASection />
      </main>
      
      <Footer />
    </div>
  );
};

export default LandingPage;
